import { motion } from 'framer-motion';
import { Calendar, MapPin, PlusCircle, Save, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { vendorApi } from '../../Vender/vendorApi.js';
import Input from '../../ui/input.js';
import { stockInApi } from './stockInApi.js';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const getYesterdayDate = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

// Helper to format date to YYYY-MM-DD format for date inputs
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  // Try to parse and format
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};


const Stock_in = () => {
  const location = useLocation();
  const createEmptyRow = () => ({
    stockName: '',
    stock_item_id: null,
    quantityOrWeight: '',
    pricePerUnit: '',
    totalPrice: '',
    vendor_id: '',
    vendorName: '',
    expDate: '',
    ledger_id: null, // For editing existing entries
  });

  // Initialize from location state if available
  const initialState = location.state || {};
  const hasItemsFromState = initialState.items && Array.isArray(initialState.items) && initialState.items.length > 0;

  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [loadingStockItems, setLoadingStockItems] = useState(false);

  const [date, setDate] = useState(() => {
    const yesterday = getYesterdayDate();
    const today = getTodayDate();
    const initialDate = initialState.date || today;
    // Ensure initial date is within allowed range (yesterday or today)
    if (initialDate >= yesterday && initialDate <= today) {
      return initialDate;
    }
    return today;
  });
  const [formSiteId, setFormSiteId] = useState(() => initialState.siteId || ''); // Site ID for form mode
  const [stockRows, setStockRows] = useState(() => {
    if (hasItemsFromState) {
      return initialState.items.map(item => ({
        id: item.id,
        ledger_id: item.ledger_id || item.id, // Use ledger_id for editing
        stock_item_id: item.stock_item_id || item.stockItemId || null,
        stockName: item.item_name || item.stockName || '',
        quantityOrWeight: item.quantityOrWeight || '',
        pricePerUnit: item.pricePerUnit ||'',
        totalPrice: item.totalPrice || '',
        vendor_id: item.vendor_id || '',
        vendorName: item.vendor_name || item.vendorName || '',
        expDate: formatDateForInput(item.expDate || item.exp_date || item.expiry_date || ''),
        reference_id: item.reference_id || '',
        notes: item.notes || '',
      }));
    }
    return [createEmptyRow()];
  });
  const [errors, setErrors] = useState({
    date: '',
    siteId: '',
    rows: [{}],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [savedState, setSavedState] = useState(null);
  const [currentStockInId, setCurrentStockInId] = useState(() => {
    if (hasItemsFromState && initialState.items[0]?.id) {
      return initialState.items[0].id;
    }
    return null;
  }); // ID of current stock-in entry being edited
  
  // Track original entries (with ledger_id) when loaded for editing
  // This helps us identify which entries were deleted when saving
  const [originalEntries, setOriginalEntries] = useState(() => {
    if (hasItemsFromState) {
      return initialState.items
        .filter(item => item.ledger_id || item.id)
        .map(item => ({
          ledger_id: item.ledger_id || item.id,
          stock_item_id: item.stock_item_id || item.stockItemId || null,
        }));
    }
    return [];
  });

  const hasExistingEntries = stockRows.some((row) => {
    if (!row) {
      return false;
    }

    // Check for ledger_id (primary identifier for editing stock-in entries)
    if (row?.ledger_id !== undefined && row?.ledger_id !== null) {
      return true;
    }

    if (row?.id !== undefined && row?.id !== null) {
      return true;
    }

    if (row?.isExisting || row?.isPersisted || row?.persisted) {
      return true;
    }

    return false;
  });
  const canEditRows = !hasExistingEntries || isEditing;
  const shouldShowEditButton = hasExistingEntries || hasItemsFromState;
  const shouldShowSaveButton = !hasExistingEntries || isEditing;

  // Load sites on component mount
  useEffect(() => {
    const loadSites = async () => {
      setLoadingSites(true);
      try {
        const sitesData = await stockInApi.getSites();
        // Sort sites alphabetically by site_name
        const sortedSites = (sitesData || []).sort((a, b) => {
          const nameA = (a.site_name || '').toLowerCase();
          const nameB = (b.site_name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setSites(sortedSites);
      } catch (err) {
        console.error('Error loading sites:', err);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };

    loadSites();
  }, []);

  // Load vendors on component mount
  useEffect(() => {
    const loadVendors = async () => {
      setLoadingVendors(true);
      try {
        const result = await vendorApi.listVendors({
          limit: 100,
          status: 'active', // Only load active vendors
        });

        if (result.success && result.vendors) {
          // Store vendors with both id and name for vendor_id support
          const vendorsList = result.vendors
            .filter(vendor => vendor.vendor_name && vendor.vendor_name.trim().length > 0)
            .map(vendor => ({
              id: vendor.vendor_id || vendor.id,
              name: vendor.vendor_name,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically
          
          setVendors(vendorsList);
        } else {
          console.warn('No vendors found or API error');
          setVendors([]);
        }
      } catch (err) {
        console.error('Error loading vendors:', err);
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    loadVendors();
  }, []);

  // Load stock items on component mount and when site changes
  useEffect(() => {
    const loadStockItems = async () => {
      setLoadingStockItems(true);
      try {
        // Try to load stock items, optionally filtered by site
        const params = {};
        if (formSiteId) {
          params.site_id = formSiteId;
        }

        let result = await stockInApi.getStockItems(params);

        // If the API call fails with a backend error (like missing index), try without site filter
        if (!result.success && formSiteId && result.error && result.message && result.message.includes('index')) {
          console.warn('Site-filtered query failed (backend index issue), trying without site filter...');
          // Retry without site filter as fallback
          result = await stockInApi.getStockItems({});
        }

        if (result.success && result.items) {
          // Map items to include both name and ID
          // Note: exp_date is now an alias of expiry_date from the API
          const items = result.items.map(item => ({
            id: item.stock_item_id || item.id || item._id,
            name: item.stock_name || item.name || item.item_name || 'Unknown Item',
            stock_item_id: item.stock_item_id || item.id || item._id,
            exp_date: item.exp_date || item.expiry_date, // Prefer exp_date
            expiry_date: item.exp_date || item.expiry_date, // Keep for compatibility
          })).sort((a, b) => a.name.localeCompare(b.name));
          
          console.log('Loaded stock items:', items.length, 'items');
          setStockItems(items);
        } else {
          console.warn('No stock items found or API error:', result.error || result.message);
          setStockItems([]);
        }
      } catch (err) {
        console.error('Error loading stock items:', err);
        setStockItems([]);
      } finally {
        setLoadingStockItems(false);
      }
    };

    loadStockItems();
  }, [formSiteId]);


  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const updateView = (event) => {
      setIsMobileView(event.matches);
    };

    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  const handleDateChange = (value) => {
    const yesterday = getYesterdayDate();
    const today = getTodayDate();
    // Validate that the selected date is within allowed range (yesterday or today)
    if (value >= yesterday && value <= today) {
      setDate(value);
      setErrors((prev) => ({ ...prev, date: '' }));
      setSuccess('');
      setError('');
    } else {
      // If date is outside range, set error
      setErrors((prev) => ({ ...prev, date: 'Date must be yesterday or today' }));
    }
  };

  const handleSiteChange = (value) => {
    setFormSiteId(value);
    setErrors((prev) => ({ ...prev, siteId: '', rows: [{}] }));
    setSuccess('');
    setError('');
    // Clear all stock rows and reset to a single empty row when site changes
    setStockRows([createEmptyRow()]);
  };

  const handleRowChange = (index, field, value) => {
    setStockRows((prevRows) => {
      const nextRows = [...prevRows];
      const updatedRow = { ...nextRows[index], [field]: value };

      if (field === 'quantityOrWeight' || field === 'pricePerUnit') {
        const quantity =
          parseFloat(
            field === 'quantityOrWeight' ? value : updatedRow.quantityOrWeight
          ) || 0;
        const price =
          parseFloat(field === 'pricePerUnit' ? value : updatedRow.pricePerUnit) ||
          0;
        const total = quantity * price;
        updatedRow.totalPrice = total > 0 ? total.toFixed(2) : '';
      }

      nextRows[index] = updatedRow;
      return nextRows;
    });

    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.map((err, idx) =>
        idx === index ? { ...err, [field]: '' } : err
      ),
    }));

    setSuccess('');
    setError('');
  };

  const handleAddRow = () => {
    if (!canEditRows) {
      return;
    }
    setStockRows((prev) => [...prev, createEmptyRow()]);
    setErrors((prev) => ({
      ...prev,
      rows: [...prev.rows, {}],
    }));
  };

  const handleClearRow = (index) => {
    if (!canEditRows) {
      return;
    }
    setStockRows((prev) =>
      prev.map((row, idx) => (idx === index ? createEmptyRow() : row))
    );
    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.map((rowError, idx) =>
        idx === index ? {} : rowError
      ),
    }));
  };

  const handleRemoveRow = (index) => {
    if (!canEditRows) {
      return;
    }
    // Allow removing the last row in edit mode if it has a ledger_id (existing entry that can be deleted)
    if (stockRows.length === 1) {
      if (isEditing && stockRows[0]?.ledger_id) {
        // Allow removing the last row in edit mode if it's an existing entry
      } else {
        // Prevent removing the last row if it's not in edit mode or doesn't have ledger_id
        return;
      }
    }

    setStockRows((prev) => prev.filter((_, idx) => idx !== index));
    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.filter((_, idx) => idx !== index),
    }));
  };

  // Validate form
  const validateForm = () => {
    const rowErrors = stockRows.map((row) => {
      const rowError = {};

      if (!row.stockName.trim()) {
        rowError.stockName = 'Required';
      }

      if (!row.quantityOrWeight) {
        rowError.quantityOrWeight = 'Required';
      } else if (parseFloat(row.quantityOrWeight) <= 0) {
        rowError.quantityOrWeight = 'Must be greater than 0';
      }

      if (!row.pricePerUnit) {
        rowError.pricePerUnit = 'Required';
      } else if (parseFloat(row.pricePerUnit) <= 0) {
        rowError.pricePerUnit = 'Must be greater than 0';
      }

      if (!row.vendorName) {
        rowError.vendorName = 'Required';
      }

      if (!row.expDate) {
        rowError.expDate = 'Required';
      }

      return rowError;
    });

    const dateError = date ? '' : 'Date is required';
    const siteError = formSiteId ? '' : 'Site is required';
    const hasRowErrors = rowErrors.some(
      (rowError) => Object.keys(rowError).length > 0
    );

    setErrors({
      date: dateError,
      siteId: siteError,
      rows: rowErrors,
    });

    return !dateError && !siteError && !hasRowErrors;
  };

  const isFormReady =
    !!date &&
    !!formSiteId &&
    stockRows.every((row) => {
      const hasName = row.stockName.trim().length > 0;
      const quantity = parseFloat(row.quantityOrWeight);
      const price = parseFloat(row.pricePerUnit);
      const hasQuantity = !!row.quantityOrWeight && quantity > 0;
      const hasPrice = !!row.pricePerUnit && price > 0;
      const hasVendor = !!row.vendorName;
      const hasExpDate = !!row.expDate;
      return hasName && hasQuantity && hasPrice && hasVendor && hasExpDate;
    });

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fill in all required fields correctly');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const siteId = formSiteId || (hasItemsFromState ? initialState.siteId : '');
      const siteName = sites.find(s => (s.site_id || s.id) === siteId)?.site_name || 
                      (hasItemsFromState ? initialState.siteName : '');

      // For each item, find the stock item and create a stock-in transaction
      // Note: The new API requires stock_item_id, so we need to find existing stock items first
      const results = [];
      const errors = [];

      // If we're editing existing entries, check for deleted entries
      // Compare original entries with current rows to find which ones were removed
      if (hasExistingEntries && originalEntries.length > 0) {
        const currentLedgerIds = stockRows
          .filter(row => row.ledger_id)
          .map(row => row.ledger_id);
        
        const deletedEntries = originalEntries.filter(
          original => !currentLedgerIds.includes(original.ledger_id)
        );

        // Void removed entries from the backend (recommended "remove a row" behavior)
        for (const deletedEntry of deletedEntries) {
          try {
            console.log(`Voiding removed stock-in entry with ledger_id: ${deletedEntry.ledger_id}`);
            const voidResult = await stockInApi.voidStockInEntry(deletedEntry.ledger_id, {
              reason: 'Removed by user during edit'
            });
            
            if (voidResult.success) {
              results.push({
                item: `Voided entry (ledger_id: ${deletedEntry.ledger_id})`,
                success: true,
                voided: true,
              });
            } else {
              errors.push(`Failed to void stock-in entry (ledger_id: ${deletedEntry.ledger_id}): ${voidResult.message || voidResult.error}`);
            }
          } catch (voidErr) {
            console.error(`Error voiding stock-in entry (ledger_id: ${deletedEntry.ledger_id}):`, voidErr);
            errors.push(`Error voiding stock-in entry (ledger_id: ${deletedEntry.ledger_id}): ${voidErr.message || 'Unknown error'}`);
          }
        }
      }

      for (let i = 0; i < stockRows.length; i++) {
        const row = stockRows[i];
        
        try {
          // Check if we're editing an existing entry (has ledger_id)
          const isEditingEntry = row.ledger_id && hasExistingEntries;

          if (isEditingEntry) {
            // Update existing stock-in entry using ledger_id
            const quantity = parseFloat(row.quantityOrWeight);
            if (!quantity || quantity <= 0) {
              errors.push(`Invalid quantity for "${row.stockName}". Quantity must be greater than 0.`);
              continue;
            }

            const updateData = {
              quantity: quantity,
              unit_cost: parseFloat(row.pricePerUnit) || 0,
              ...(row.vendor_id ? { vendor_id: row.vendor_id } : {}),
              ...(row.vendorName && !row.vendor_id ? { vendor_name: row.vendorName } : {}),
              ...(row.expDate ? { expiry_date: row.expDate } : {}),
              ...(row.reference_id ? { reference_id: row.reference_id } : {}),
              ...(row.notes ? { notes: row.notes } : {}),
            };

            const result = await stockInApi.updateStockInEntry(row.ledger_id, updateData);
            
            if (result.success) {
              results.push({
                item: row.stockName,
                success: true,
                data: result.data,
                entry: result.entry,
              });
            } else {
              errors.push(`Failed to update stock-in for "${row.stockName}": ${result.message || result.error}`);
            }
          } else {
            // Create new stock-in transaction
            let stockItemId = null;

            // If we have a stock_item_id from the existing item, use it first
            if (row.stock_item_id) {
              stockItemId = row.stock_item_id;
            } else {
              // Try to find the stock item by name
              try {
                const stockItemsResult = await stockInApi.getStockItems({
                  name: row.stockName.trim(),
                  site_id: siteId,
                });

                if (stockItemsResult.success && stockItemsResult.items && stockItemsResult.items.length > 0) {
                  // Use the first matching item
                  stockItemId = stockItemsResult.items[0].stock_item_id || 
                               stockItemsResult.items[0].id ||
                               stockItemsResult.items[0]._id;
                } else if (stockItemsResult.error) {
                  // If API returns an error (e.g., backend index issue), log it but continue
                  console.warn(`Stock In API: Could not find stock item "${row.stockName}":`, stockItemsResult.error);
                  // Check if it's a backend infrastructure error
                  if (stockItemsResult.message && stockItemsResult.message.includes('index')) {
                    console.warn('Backend database index issue detected. This is a backend configuration problem.');
                  }
                }
              } catch (apiError) {
                // Handle API errors gracefully
                console.error(`Stock In API: Error searching for stock item "${row.stockName}":`, apiError);
                // Continue processing other items even if this one fails
              }
            }

            if (!stockItemId) {
              errors.push(`Stock item "${row.stockName}" not found. Please ensure the item exists in the system or create it first.`);
              continue;
            }

            // Validate quantity
            const quantity = parseFloat(row.quantityOrWeight);
            if (!quantity || quantity <= 0) {
              errors.push(`Invalid quantity for "${row.stockName}". Quantity must be greater than 0.`);
              continue;
            }

            // Prepare stock-in transaction data according to API spec
            const stockInTransactionData = {
              quantity: quantity,
              unit_cost: parseFloat(row.pricePerUnit) || 0,
              ...(siteId ? { site_id: siteId } : {}),
              ...(siteName ? { site_name: siteName } : {}),
              ...(row.vendor_id ? { vendor_id: row.vendor_id } : {}),
              ...(row.vendorName && !row.vendor_id ? { vendor_name: row.vendorName } : {}),
              ...(row.expDate ? { expiry_date: row.expDate } : {}),
              ...(row.reference_id ? { reference_id: row.reference_id } : {}),
              ...(row.notes ? { notes: row.notes } : {}),
            };

            // Create stock-in transaction
            const result = await stockInApi.createStockInTransaction(stockItemId, stockInTransactionData);
            
            if (result.success) {
              results.push({
                item: row.stockName,
                success: true,
                data: result.data,
              });
            } else {
              errors.push(`Failed to create stock-in for "${row.stockName}": ${result.message || result.error}`);
            }
          }
        } catch (err) {
          console.error(`Error processing item "${row.stockName}":`, err);
          errors.push(`Error processing "${row.stockName}": ${err.message || 'Unknown error'}`);
        }
      }

      // Handle results
      if (errors.length > 0 && results.length === 0) {
        // All failed
        setError(`Failed to create stock-in entries:\n${errors.join('\n')}`);
      } else if (errors.length > 0 && results.length > 0) {
        // Partial success
        setSuccess(`${results.length} item(s) processed successfully.`);
        setError(`Some items failed:\n${errors.join('\n')}`);
      } else if (results.length > 0) {
        // All succeeded
        setSuccess(`${results.length} stock-in transaction(s) created successfully!`);
        
        if (hasExistingEntries) {
          // Existing data updated, exit edit mode but keep values
          // Update originalEntries to reflect current state (only entries that still exist)
          const updatedOriginalEntries = stockRows
            .filter(row => row.ledger_id)
            .map(row => ({
              ledger_id: row.ledger_id,
              stock_item_id: row.stock_item_id || null,
            }));
          setOriginalEntries(updatedOriginalEntries);
          setIsEditing(false);
          setSavedState(null);
          setErrors({ date: '', siteId: '', rows: stockRows.map(() => ({})) });
        } else {
          // Reset form after creating new entry
          setDate(getTodayDate());
          setFormSiteId('');
          setStockRows([createEmptyRow()]);
          setErrors({ date: '', siteId: '', rows: [{}] });
          setIsEditing(false);
          setSavedState(null);
          setCurrentStockInId(null);
          setOriginalEntries([]);
        }
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to save stock entry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleEdit = () => {
    if (!hasExistingEntries || isLoading) {
      return;
    }

    if (isEditing) {
      if (savedState) {
        setDate(savedState.date);
        setFormSiteId(savedState.siteId || '');
        setStockRows(savedState.rows);
        setErrors({
          date: '',
          siteId: '',
          rows: savedState.rows.map(() => ({})),
        });
      } else {
        setDate(getTodayDate());
        setFormSiteId('');
        setStockRows([createEmptyRow()]);
        setErrors({ date: '', siteId: '', rows: [{}] });
      }
      setSuccess('');
      setError('');
      setIsEditing(false);
      setSavedState(null);
      return;
    }

    setSavedState({
      date,
      siteId: formSiteId,
      rows: stockRows.map((row) => ({ ...row })),
    });
    setSuccess('');
    setError('');
    setErrors((prev) => ({
      ...prev,
      rows: stockRows.map(() => ({})),
    }));
    setIsEditing(true);
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  return (
    <div className="w-full h-full min-h-screen">
      <div className="mx-auto h-auto">
        <form onSubmit={handleSubmit} className="h-full space-y-8">
          {/* Main Form Section */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="rounded-3xl">
              {/* Date Picker and Site Selection */}
              <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-4">
                <div className="flex-1 flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                  <label
                        htmlFor="site"
                        className="inline-flex items-center gap-2"
                      >
                        Date
                      </label>
                    <div className="relative mt-1 max-w-xs">
                      <Calendar className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        id="date"
                        value={date}
                        onChange={(e) => handleDateChange(e.target.value)}
                        min={getYesterdayDate()}
                        max={getTodayDate()}
                        disabled={hasItemsFromState || !canEditRows}
                        readOnly={hasItemsFromState}
                        className={`w-full h-12 rounded-2xl border pl-10 pr-4 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed ${
                          hasItemsFromState 
                            ? 'bg-gray-100 border-gray-200' 
                            : errors.date
                            ? 'bg-white border-red-500 focus:ring-red-500'
                            : 'bg-white border-gray-200 focus:ring-green-500'
                        }`}
                        required
                      />
                    </div>
                    {errors.date && (
                      <p className="mt-1 text-xs text-red-600">{errors.date}</p>
                    )}
                  </div>
                  {!hasItemsFromState && (
                    <div className="flex-1">
                      <label
                        htmlFor="site"
                        className="inline-flex items-center gap-2"
                      >
                        Site
                      </label>
                      <div className="relative mt-1 max-w-xs">
                        <MapPin className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <select
                          id="site"
                          value={formSiteId}
                          onChange={(e) => handleSiteChange(e.target.value)}
                          disabled={!canEditRows || loadingSites}
                          className={`w-full h-12 rounded-2xl border bg-white pl-10 pr-4 text-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                            errors.siteId
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-gray-200 focus:ring-green-500'
                          }`}
                          required
                        >
                          <option value="" disabled hidden>
                            {loadingSites ? 'Loading sites...' : 'Select site'}
                          </option>
                          {sites.map((site) => (
                            <option key={site.site_id} value={site.site_id}>
                              {site.site_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {errors.siteId && (
                        <p className="mt-1 text-xs text-red-600">{errors.siteId}</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
                  {shouldShowEditButton && (
                    <button
                      type="button"
                      onClick={handleToggleEdit}
                      className={`w-full md:w-auto min-w-[120px] h-12 rounded-lg px-6 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 flex items-center justify-center ${
                        isEditing
                          ? 'bg-red-500 hover:bg-red-600 focus-visible:ring-red-500'
                          : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500'
                      }`}
                      style={{ color: '#ffffff' }}
                    >
                      <span
                        style={{
                          color: '#ffffff !important',
                          WebkitTextFillColor: '#ffffff',
                          WebkitTextStroke: 'none',
                        }}
                      >
                        {isEditing ? 'Cancel' : 'Edit'}
                      </span>
                    </button>
                  )}
                  {shouldShowSaveButton && (
                    <button
                      type="submit"
                      disabled={isLoading || !isFormReady || !canEditRows}
                      className={`
                      w-full md:w-auto min-w-[140px] h-12 px-6 py-2 bg-green-700 rounded-lg
                      inline-flex flex-col justify-center items-center gap-2
                      text-center justify-center text-base font-bold font-['Figtree'] leading-normal
                      transition-all duration-300 ease-in-out
                      hover:shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)]
                      hover:shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)]
                      hover:shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)]
                      hover:outline hover:outline-1 hover:outline-offset-[-1px] hover:outline-white
                      hover:backdrop-blur-[30px]
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none
                      disabled:hover:outline-none disabled:hover:backdrop-blur-none
                      text-white
                    `}
                      style={{ color: '#ffffff' }}
                    >
                      <span
                        style={{
                          color: '#ffffff !important',
                          WebkitTextFillColor: '#ffffff',
                          WebkitTextStroke: 'none',
                        }}
                        className="flex items-center gap-2"
                      >
                        {isLoading ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-5 h-5" />
                            Save
                          </>
                        )}
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {/* Stock Table */}
              <div className="relative group">
                {!isMobileView && (
                  <>
                    <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                      <table className="min-w-full border-collapse text-sm md:text-base">
                        <thead>
                          <tr className="bg-gray-50 text-left text-gray-600">
                            <th className="px-4 py-3 font-semibold">S.No</th>
                            <th className="px-4 py-3 font-semibold min-w-[160px]">
                              Item Name
                            </th>
                            <th className="px-4 py-3 font-semibold">
                              Quantity (In weight)
                            </th>
                            <th className="px-4 py-3 font-semibold min-w-[120px]">
                              Price per Kg
                            </th>
                            <th className="px-4 py-3 font-semibold min-w-[120px]">
                              Total Cost
                            </th>
                            <th className="px-4 py-3 font-semibold">Exp Date</th>
                            <th className="px-4 py-3 font-semibold min-w-[160px]">
                              Vendor Name
                            </th>
                            <th className="px-4 py-3 font-semibold text-right"> </th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockRows.map((row, index) => (
                            <tr
                              key={`stock-row-${index}`}
                              className="border-t border-gray-100"
                            >
                              <td className="px-4 py-4 align-top text-gray-800">
                                {index + 1}
                              </td>
                              <td className="px-1 py-1 min-w-[160px]">
                                <div className="relative">
                                  <select
                                    value={row.stock_item_id || ''}
                                    onChange={(e) => {
                                      const selectedItemId = e.target.value;
                                      const selectedItem = stockItems.find(item => item.id === selectedItemId);
                                      if (selectedItem) {
                                        handleRowChange(index, 'stock_item_id', selectedItemId);
                                        handleRowChange(index, 'stockName', selectedItem.name);
                                      } else {
                                        handleRowChange(index, 'stock_item_id', '');
                                        handleRowChange(index, 'stockName', '');
                                      }
                                    }}
                                    disabled={!canEditRows || loadingStockItems}
                                    className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm shadow-none focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                      errors.rows[index]?.stockName
                                        ? 'border-red-500 focus:ring-red-500'
                                        : 'border-gray-200 focus:ring-green-500'
                                    }`}
                                  >
                                    <option value="">
                                      {loadingStockItems ? 'Loading items...' : 'Select item'}
                                    </option>
                                    {stockItems.map((item) => (
                                      <option key={item.id} value={item.id}>
                                        {item.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {errors.rows[index]?.stockName && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {errors.rows[index].stockName}
                                  </p>
                                )}
                              </td>
                              <td className="px-1 py-1 min-w-[160px]">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={row.quantityOrWeight}
                                  onChange={(e) =>
                                    handleRowChange(
                                      index,
                                      'quantityOrWeight',
                                      e.target.value
                                    )
                                  }
                                  placeholder="0.00"
                                  disabled={!canEditRows}
                                  className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                    errors.rows[index]?.quantityOrWeight
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-200 focus:ring-green-500'
                                  }`}
                                />
                                {errors.rows[index]?.quantityOrWeight && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {errors.rows[index].quantityOrWeight}
                                  </p>
                                )}
                              </td>
                              <td className="px-1 py-1 min-w-[120px]">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={row.pricePerUnit}
                                  onChange={(e) =>
                                    handleRowChange(index, 'pricePerUnit', e.target.value)
                                  }
                                  placeholder="0.00"
                                  disabled={!canEditRows}
                                  className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                    errors.rows[index]?.pricePerUnit
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-200 focus:ring-green-500'
                                  }`}
                                />
                                {errors.rows[index]?.pricePerUnit && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {errors.rows[index].pricePerUnit}
                                  </p>
                                )}
                              </td>
                              <td className="px-1 py-1 min-w-[120px]">
                                <Input
                                  type="number"
                                  value={row.totalPrice}
                                  readOnly
                                  placeholder="0.00"
                                  className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600 shadow-none"
                                />
                              </td>
                              <td className="px-1 py-1">
                                <input
                                  type="date"
                                  value={row.expDate}
                                  onChange={(e) =>
                                    handleRowChange(index, 'expDate', e.target.value)
                                  }
                                  disabled={!canEditRows}
                                  className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 ${
                                    errors.rows[index]?.expDate
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-200 focus:ring-green-500'
                                  }`}
                                />
                                {errors.rows[index]?.expDate && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {errors.rows[index].expDate}
                                  </p>
                                )}
                              </td>
                              <td className="px-1 py-1">
                                <select
                                  value={(() => {
                                    // Match by vendor_id first, then by vendorName
                                    if (row.vendor_id) {
                                      const vendor = vendors.find(v => typeof v === 'object' && v.id === row.vendor_id);
                                      return vendor ? vendor.id : (row.vendorName || '');
                                    }
                                    return row.vendorName || '';
                                  })()}
                                  onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    // Find vendor by id or name
                                    const selectedVendor = vendors.find(v => {
                                      if (typeof v === 'object') {
                                        return v.id === selectedValue || v.name === selectedValue;
                                      }
                                      return v === selectedValue;
                                    });
                                    if (selectedVendor) {
                                      const vendorId = typeof selectedVendor === 'object' ? selectedVendor.id : '';
                                      const vendorName = typeof selectedVendor === 'object' ? selectedVendor.name : selectedVendor;
                                      handleRowChange(index, 'vendor_id', vendorId);
                                      handleRowChange(index, 'vendorName', vendorName);
                                    } else {
                                      handleRowChange(index, 'vendor_id', '');
                                      handleRowChange(index, 'vendorName', '');
                                    }
                                  }}
                                  disabled={!canEditRows || loadingVendors}
                                  className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                    errors.rows[index]?.vendorName
                                      ? 'border-red-500 focus:ring-red-500'
                                      : 'border-gray-200 focus:ring-green-500'
                                  }`}
                                >
                                  <option value="" disabled hidden>
                                    {loadingVendors ? 'Loading vendors...' : 'Select vendor'}
                                  </option>
                                  {vendors.length > 0 ? (
                                    vendors.map((vendor, vendorIndex) => {
                                      const vendorId = typeof vendor === 'object' ? vendor.id : '';
                                      const vendorName = typeof vendor === 'object' ? vendor.name : vendor;
                                      return (
                                        <option key={vendorIndex} value={vendorId || vendorName}>
                                          {vendorName}
                                        </option>
                                      );
                                    })
                                  ) : (
                                    !loadingVendors && (
                                      <option value="" disabled>
                                        No vendors available
                                      </option>
                                    )
                                  )}
                                </select>
                                {errors.rows[index]?.vendorName && (
                                  <p className="mt-1 text-xs text-red-600">
                                    {errors.rows[index].vendorName}
                                  </p>
                                )}
                              </td>
                              <td className="px-1 py-1 text-right">
                                <button
                                  type="button"
                                  onClick={() => {
                                    // In edit mode, index 0 should behave like Close (remove row)
                                    if (isEditing && index === 0) {
                                      handleRemoveRow(index);
                                    } else if (index === 0) {
                                      handleClearRow(index);
                                    } else {
                                      handleRemoveRow(index);
                                    }
                                  }}
                                  disabled={!canEditRows || (index !== 0 && stockRows.length === 1) || (isEditing && index === 0 && stockRows.length === 1 && !stockRows[0].ledger_id)}
                                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                    (index === 0 && !isEditing)
                                      ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                                      : stockRows.length === 1 && !isEditing
                                        ? 'cursor-not-allowed border-gray-200 text-gray-300'
                                        : 'border-red-100 text-red-600 hover:border-red-300 hover:text-red-700'
                                  }`}
                                  aria-label={(isEditing && index === 0) || index !== 0 ? 'Remove item row' : 'Clear row values'}
                                >
                                  <XCircle className="h-4 w-4" />
                                  {(isEditing && index === 0) || index !== 0 ? 'Close' : 'Clear'}
                                </button>
                              </td>
                            </tr>
                          ))}
                          <tr className="border-t border-gray-200 bg-gray-50 text-gray-700 font-semibold">
                            <td className="px-4 py-3" colSpan={4}>
                              Total Product Cost
                            </td>
                            <td className="px-1 py-3">
                              <div className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-right">
                                {stockRows
                                  .reduce(
                                    (sum, row) => sum + (parseFloat(row.totalPrice) || 0),
                                    0
                                  )
                                  .toFixed(2)}
                              </div>
                            </td>
                            <td className="px-1 py-3" colSpan={3}></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="pointer-events-none mt-4 flex justify-center">
                      <button
                        type="button"
                        onClick={handleAddRow}
                                  disabled={!canEditRows}
                        className="pointer-events-auto inline-flex translate-y-2 items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 focus-visible:translate-y-0 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <PlusCircle className="h-4 w-4" />
                        Add Item
                      </button>
                    </div>
                  </>
                )}

                {isMobileView && (
                  <div className="space-y-4 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
                    {stockRows.map((row, index) => (
                      <div
                        key={`stock-card-${index}`}
                        className="rounded-2xl border border-gray-100 p-4"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-700">
                            Item #{index + 1}
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              // In edit mode, index 0 should behave like Close (remove row)
                              if (isEditing && index === 0) {
                                handleRemoveRow(index);
                              } else if (index === 0) {
                                handleClearRow(index);
                              } else {
                                handleRemoveRow(index);
                              }
                            }}
                            disabled={!canEditRows || (index !== 0 && stockRows.length === 1) || (isEditing && index === 0 && stockRows.length === 1 && !stockRows[0].ledger_id)}
                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              (index === 0 && !isEditing)
                                ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                                : stockRows.length === 1 && !isEditing
                                  ? 'cursor-not-allowed border-gray-200 text-gray-300'
                                  : 'border-red-100 text-red-600 hover:border-red-300 hover:text-red-700'
                            }`}
                          >
                            <XCircle className="h-4 w-4" />
                            {(isEditing && index === 0) || index !== 0 ? 'Close' : 'Clear'}
                          </button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Item Name
                            </label>
                            <div className="relative">
                              <select
                                value={row.stock_item_id || ''}
                                onChange={(e) => {
                                  const selectedItemId = e.target.value;
                                  const selectedItem = stockItems.find(item => item.id === selectedItemId);
                                  if (selectedItem) {
                                    handleRowChange(index, 'stock_item_id', selectedItemId);
                                    handleRowChange(index, 'stockName', selectedItem.name);
                                  } else {
                                    handleRowChange(index, 'stock_item_id', '');
                                    handleRowChange(index, 'stockName', '');
                                  }
                                }}
                                disabled={!canEditRows || loadingStockItems}
                                className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm shadow-none focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                  errors.rows[index]?.stockName
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 focus:ring-green-500'
                                }`}
                              >
                                <option value="">
                                  {loadingStockItems ? 'Loading items...' : 'Select item'}
                                </option>
                                {stockItems.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {errors.rows[index]?.stockName && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.rows[index].stockName}
                              </p>
                            )}
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Quantity (In weight)
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.quantityOrWeight}
                                onChange={(e) =>
                                  handleRowChange(index, 'quantityOrWeight', e.target.value)
                                }
                                placeholder="0.00"
                                  disabled={!canEditRows}
                                className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                  errors.rows[index]?.quantityOrWeight
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 focus:ring-green-500'
                                }`}
                              />
                              {errors.rows[index]?.quantityOrWeight && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.rows[index].quantityOrWeight}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Price per Kg
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.pricePerUnit}
                                onChange={(e) =>
                                  handleRowChange(index, 'pricePerUnit', e.target.value)
                                }
                                placeholder="0.00"
                                disabled={!canEditRows}
                                className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                  errors.rows[index]?.pricePerUnit
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 focus:ring-green-500'
                                }`}
                              />
                              {errors.rows[index]?.pricePerUnit && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.rows[index].pricePerUnit}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Total Cost
                              </label>
                              <Input
                                type="number"
                                value={row.totalPrice}
                                readOnly
                                placeholder="0.00"
                                className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600 shadow-none"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Exp Date
                              </label>
                              <input
                                type="date"
                                value={row.expDate}
                                onChange={(e) =>
                                  handleRowChange(index, 'expDate', e.target.value)
                                }
                              disabled={!canEditRows}
                                className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 ${
                                  errors.rows[index]?.expDate
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 focus:ring-green-500'
                                }`}
                              />
                              {errors.rows[index]?.expDate && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.rows[index].expDate}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Vendor Name
                              </label>
                              <select
                                value={row.vendor_id || row.vendorName || ''}
                                onChange={(e) => {
                                  const selectedValue = e.target.value;
                                  // Find vendor by id or name
                                  const selectedVendor = vendors.find(v => 
                                    (typeof v === 'object' ? (v.id === selectedValue || v.name === selectedValue) : v === selectedValue)
                                  );
                                  if (selectedVendor) {
                                    const vendorId = typeof selectedVendor === 'object' ? selectedVendor.id : '';
                                    const vendorName = typeof selectedVendor === 'object' ? selectedVendor.name : selectedVendor;
                                    handleRowChange(index, 'vendor_id', vendorId);
                                    handleRowChange(index, 'vendorName', vendorName);
                                  } else {
                                    handleRowChange(index, 'vendor_id', '');
                                    handleRowChange(index, 'vendorName', '');
                                  }
                                }}
                                disabled={!canEditRows || loadingVendors}
                                className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                  errors.rows[index]?.vendorName
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-200 focus:ring-green-500'
                                }`}
                              >
                                <option value="" disabled hidden>
                                  {loadingVendors ? 'Loading vendors...' : 'Select vendor'}
                                </option>
                                {vendors.length > 0 ? (
                                  vendors.map((vendor, vendorIndex) => {
                                    const vendorId = typeof vendor === 'object' ? vendor.id : '';
                                    const vendorName = typeof vendor === 'object' ? vendor.name : vendor;
                                    return (
                                      <option key={vendorIndex} value={vendorId || vendorName}>
                                        {vendorName}
                                      </option>
                                    );
                                  })
                                ) : (
                                  !loadingVendors && (
                                    <option value="" disabled>
                                      No vendors available
                                    </option>
                                  )
                                )}
                              </select>
                              {errors.rows[index]?.vendorName && (
                                <p className="mt-1 text-xs text-red-600">
                                  {errors.rows[index].vendorName}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleAddRow}
                        disabled={!canEditRows}
                        className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <PlusCircle className="h-4 w-4" />
                        Add Item
                      </button>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-right text-sm font-semibold text-gray-700">
                      Total Product Cost:{' '}
                      {stockRows
                        .reduce(
                          (sum, row) => sum + (parseFloat(row.totalPrice) || 0),
                          0
                        )
                        .toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

            {/* Success/Error Messages */}
            {(success || error) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg ${
                  success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {success || error}
              </motion.div>
            )}
          </form>
      </div>
    </div>
  );
};

export default Stock_in;

